// Wording for the slots block: what a hold's state means, and the instruction "Pick other times"
// sends through the normal instruction path (the UI never edits a slots block itself; the
// operator writes the card, and holding anything still needs an approval).
import type { CalendarHoldStatus, CalendarHoldView, CalendarSlotView } from "@shared/api";
import { sameSlot, slotLabel } from "@web/lib/slots";

export interface HoldWords {
  label: string;
  tone: "calm" | "working" | "muted" | "attention";
}

export function holdWords(hold: Pick<CalendarHoldView, "status" | "kind">): HoldWords {
  const status: CalendarHoldStatus = hold.status;
  switch (status) {
    case "proposed":
      return { label: hold.kind === "event" ? "Booking…" : "Holding…", tone: "working" };
    case "held":
      return { label: "Held", tone: "calm" };
    case "confirmed":
      return { label: "Booked", tone: "calm" };
    case "released":
      return { label: "Released", tone: "muted" };
    case "failed":
      return { label: hold.kind === "event" ? "Not booked" : "Not held", tone: "attention" };
  }
}

/** The hold that sits on `slot`, if any (the newest wins when a time was held twice). */
export function holdFor(
  holds: readonly CalendarHoldView[],
  slot: { start: string; end: string },
): CalendarHoldView | null {
  const hits = holds.filter((h) => sameSlot(h, slot));
  return hits.length ? hits[hits.length - 1]! : null;
}

/** Holds of the card that are not one of the block's slots (an earlier proposal, say). */
export function holdsOutside(
  holds: readonly CalendarHoldView[],
  slots: readonly { start: string; end: string }[],
): CalendarHoldView[] {
  return holds.filter((h) => !slots.some((s) => sameSlot(h, s)));
}

/**
 * What the reader asks the operator when they pick other times. Times are named exactly (label
 * and UTC instants) so the operator can copy them into a new slots block; the runtime re-checks
 * every time against the rules before anything is held.
 */
export function otherTimesInstruction(
  picked: readonly CalendarSlotView[],
  timeZone: string,
): string {
  const lines = picked.map((s) => `- ${s.label || slotLabel(s, timeZone)} (${s.start}/${s.end})`);
  return [
    `Propose these times instead of the ones on the card (they are free on my calendar as of now, in ${timeZone}):`,
    ...lines,
    "Replace the slots block with exactly these, and keep the hold action pointing at it.",
  ].join("\n");
}
